import React, { useState } from 'react'
import { FiSearch, FiArrowRight } from 'react-icons/fi'
import {
    MdSportsGymnastics, MdMusicNote, MdFitnessCenter, MdSelfImprovement, MdAccessibilityNew,
    MdCelebration, MdSportsMma, MdSportsTennis, MdSportsBasketball, MdSportsVolleyball,
} from 'react-icons/md'

// Cada categoría es un atajo al buscador, no un filtro del backend (tipoActividad es
// demasiado grueso). `q` va aparte de `label` porque la búsqueda distingue tildes:
// «Básquetbol» no encuentra nada y «basquetbol» sí. Lista revisada contra la API en
// sept. 2026: todas devuelven resultados; si la oferta cambia, revisar esta lista.
const CATEGORIES = [
    { icon: MdSportsGymnastics, label: 'Gimnasia', q: 'gimnasia' },
    { icon: MdMusicNote, label: 'Baile', q: 'baile' },
    { icon: MdFitnessCenter, label: 'Funcional', q: 'funcional' },
    { icon: MdSelfImprovement, label: 'Yoga', q: 'yoga' },
    { icon: MdAccessibilityNew, label: 'Pilates', q: 'pilates' },
    { icon: MdCelebration, label: 'Zumba', q: 'zumba' },
    { icon: MdSportsMma, label: 'Box', q: 'box' },
    { icon: MdSportsTennis, label: 'Tenis', q: 'tenis' },
    { icon: MdSportsBasketball, label: 'Básquetbol', q: 'basquetbol' },
    { icon: MdSportsVolleyball, label: 'Vóleibol', q: 'voleibol' },
]

const formato = (n) => n.toLocaleString('es-CL')

export default function HeroSection({ onBuscar, resumen }) {
    const [query, setQuery] = useState('')

    const handleSearch = (e) => {
        e.preventDefault()
        onBuscar?.(query.trim())
    }

    // Cifras tomadas del listado real. Mientras cargan no se muestra nada: una cifra
    // inventada que luego no coincide con el listado es peor que un hueco.
    const stats = resumen ? [
        { value: formato(resumen.talleres), label: 'talleres activos' },
        resumen.recintos > 0 && { value: formato(resumen.recintos), label: 'recintos' },
        resumen.gratuitos > 0 && { value: formato(resumen.gratuitos), label: 'gratuitos' },
    ].filter(Boolean) : []

    return (
        <section className="relative w-full overflow-hidden">
            {/* BG image + overlays */}
            <div className="absolute inset-0">
                <img
                    src="https://talleres-municipales.s3.sa-east-1.amazonaws.com/imagenes_sitio_talleres/140+1.webp"
                    alt="" aria-hidden className="w-full h-full object-cover object-center"
                />
                {/* Dark gradient */}
                <div className="absolute inset-0" style={{
                    background: 'linear-gradient(160deg, rgba(8,18,32,.92) 0%, rgba(0,58,148,.82) 50%, rgba(0,74,173,.72) 100%)'
                }} />
                {/* Dot pattern */}
                <div className="absolute inset-0 opacity-[.07]" style={{
                    backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
                    backgroundSize: '28px 28px'
                }} />
            </div>

            {/* Content */}
            <div className="relative z-10 max-w-screen-xl mx-auto px-4 sm:px-6 pt-10 pb-0">

                {/* Badge */}
                <div className="flex justify-center mb-5">
                    <span className="inline-flex items-center text-[12px] font-semibold text-white/80 border border-white/20 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full">
                        Corporación Municipal de Deportes de Ñuñoa
                    </span>
                </div>

                {/* Headline */}
                <h1 className="text-center font-extrabold text-white leading-[1.1] mb-3"
                    style={{ fontSize: 'clamp(2.2rem, 5vw, 3.5rem)' }}>
                    Muévete,{' '}
                    <span style={{ color: '#feda2c' }}>crece</span>
                    <br className="hidden sm:block" />
                    {' '}y conecta
                </h1>
                <p className="text-center text-white/75 text-base sm:text-lg max-w-lg mx-auto mb-6 leading-relaxed">
                    Talleres deportivos para todas las edades en tu comunidad.
                    Inscríbete y da el primer paso hoy.
                </p>

                {/* Search bar */}
                <form onSubmit={handleSearch} className="max-w-xl mx-auto mb-4" role="search">
                    <div className="flex gap-2 p-1.5 bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20">
                        <label className="flex-1 flex items-center gap-2 px-3">
                            <FiSearch size={18} className="text-white/60 flex-shrink-0" aria-hidden />
                            <span className="sr-only">Buscar taller</span>
                            <input
                                type="search"
                                placeholder="Buscar taller: yoga, baile, funcional..."
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                className="flex-1 bg-transparent text-white placeholder-white/60 text-sm outline-none min-w-0 py-1"
                            />
                        </label>
                        <button type="submit"
                            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm text-white transition-all hover:brightness-110 active:scale-95"
                            style={{ background: '#0047bb' }}>
                            Buscar <FiArrowRight size={15} />
                        </button>
                    </div>
                </form>

                {/* Stats — una línea bajo el buscador */}
                <p className="text-center text-sm text-white/70 mb-7 min-h-[20px]">
                    {stats.map((s, i) => (
                        <span key={s.label}>
                            {i > 0 && <span className="mx-2 text-white/40" aria-hidden>·</span>}
                            <strong className="font-bold text-white">{s.value}</strong> {s.label}
                        </span>
                    ))}
                </p>

                {/* Categories */}
                <div>
                    <p className="text-center text-[11px] text-white/60 uppercase tracking-widest mb-3">
                        Categorías populares
                    </p>
                    <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-1 justify-start md:justify-center px-1">
                        {CATEGORIES.map(({ icon: Icon, label, q }) => (
                            <button key={label} type="button" onClick={() => onBuscar?.(q)}
                                className="flex-shrink-0 flex flex-col items-center gap-1.5 px-3.5 py-3 rounded-2xl border border-white/15 bg-white/5 text-white/85 hover:bg-white/15 hover:border-white/30 hover:text-white transition-all duration-200 min-w-[76px]">
                                <Icon size={22} aria-hidden />
                                <span className="text-[11px] font-semibold whitespace-nowrap">{label}</span>
                            </button>
                        ))}
                    </div>
                </div>

            </div>

            {/* Wave — fuera del contenedor para cubrir todo el ancho */}
            <div className="relative z-10" style={{ lineHeight: 0, marginTop: 24 }}>
                <svg
                    viewBox="0 0 1440 60"
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                    style={{ display: 'block', width: '100%', height: 40 }}
                >
                    <path d="M0,60 L0,30 Q360,0 720,28 Q1080,56 1440,20 L1440,60 Z" fill="#f4f6f9" />
                </svg>
            </div>
        </section>
    )
}
